import   React                       ,
       { useEffect                   ,
	     useRef                      ,
	     useState                    }  from 'react';

import   classNames                     from 'classnames';

import   styles                         from './MediaRow.module.scss';

import   Icon                           from '../../widget/images/Icon';

import { Media                       }  from '../../../dataTypes/mediaManagment/types';
import { Video                       }  from '../../../dataTypes/mediaManagment/video_types';
import { AudioSlideshow              }  from '../../../dataTypes/mediaManagment/audioSlideshow_types';

import { WWW_URLS                    }  from '../../../backendServices/urls';
import { formatStorageSize           }  from '../../../utils/utils_storage';
import { convertTimeToSeconds        ,
         formatDurationInSeconds     }  from '../../../dataTypes/timeManagment/utils';
import { getVideoTranscodingProgress ,
         getVideoTranscodingProgressDirect }  from '../../../backendServices/mediaManagment/api_transcoder';

import { useTypedSelector            ,
         useTypedDispatch             }  from '../../../store/useTypeSelector';
import { getUploadsByEntityType      }  from '../../../store/fileManagment/actions';
import { upsertTranscodingTask       ,
         removeTranscodingTask       }  from '../../../store/tasksManagment/actions';
import { TranscodingTaskStatus       }  from '../../../store/tasksManagment/types';
import { RootState                   }  from '../../../store/generalReducer';

import { refreshProductById          ,
         retrieveProductById         }  from '../../../store/productManagment/actions';


import { retryVideoTranscoding    ,
         cancelVideoTranscoding   }  from '../../../backendServices/mediaManagment/api_video';

import { retryAudioSlideshowTranscoding ,
         cancelAudioSlideshowTranscoding }  from '../../../backendServices/mediaManagment/api_audioSlideshow';


interface MediaRowProps {
  media              : Media;
  index              : number;
  onView             : (index: number, withQRcode: boolean) => void;
  onEdit             : (index: number) => void;
  onDelete           : (media: Media) => void;
  onVisibilityToggle : (id: number) => void;
  onTranscoded       : (id: number) => void;
}

export const MediaRow = ({ media              ,
                           index              ,
                           onView             ,
                           onEdit             ,
                           onDelete           ,
                           onVisibilityToggle ,
                           onTranscoded       }: MediaRowProps) => {

  const [ transcodingProgress , setTranscodingProgress ] = useState<number | null>(null);

  const durationLabel = (() => {
    const raw = media.mediaType === 'Video'          ? (media as unknown as Video).duration
              : media.mediaType === 'AudioSlideshow' ? (media as unknown as AudioSlideshow).soundPlayingDuration
              : '';
    const secs = convertTimeToSeconds(raw ?? '');
    return secs > 0 ? formatDurationInSeconds(secs) : '—';
  })();

  const videoUploads   = useTypedSelector(state => getUploadsByEntityType(state, 'video'));
  const currentUpload = videoUploads
                           .filter(u => u.entityId === media.id)
                           .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())[0] || null;
  // `??` et NON `||` : 0 % est une vraie progression, pas une absence de valeur.
  // Avec `||`, le 0 du démarrage devenait `null` — la ligne se croyait sans upload,
  // et l'encart « Transcodage 0 % » prenait la place le temps que l'upload dépasse
  // 0 %, c'est-à-dire une seconde environ. Le transcodage n'avait alors même pas
  // été soumis : le fichier n'était pas encore arrivé.
  const uploadProgress = currentUpload?.progress ?? null;

  // Un média encore en cours de téléversement n'est PAS en transcodage : rien n'a
  // été soumis tant que le fichier n'est pas monté. Sert à retenir le sondage.
  const uploadEnCours  = uploadProgress !== null && uploadProgress < 100;

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const dispatch = useTypedDispatch();

const [showVideoChoice, setShowVideoChoice] = useState(false);
  const [ transcodingActionPending , setTranscodingActionPending ] = useState(false);

  const isVideoOrSlideshow = (media: Media): media is Video | AudioSlideshow => {
    return media.mediaType === 'Video' || media.mediaType === 'AudioSlideshow';
  };

  const isFailed = isVideoOrSlideshow(media) && media.state === -2;

  const handleRetryTranscoding = async () => {
    setTranscodingActionPending(true);
    const response = media.mediaType === 'AudioSlideshow'
                        ? await retryAudioSlideshowTranscoding(media.id, authentication.admin?.id)
                        : await retryVideoTranscoding(media.id, authentication.admin?.id);
    setTranscodingActionPending(false);

    if (response.code === 1) {
      onTranscoded(media.id);
    } else {
      console.error(`Impossible de relancer le transcodage du média ID ${media.id} : ${response.comment}`);
    }
  };

  const handleCancelTranscoding = async () => {
    setTranscodingActionPending(true);
    const response = media.mediaType === 'AudioSlideshow'
                        ? await cancelAudioSlideshowTranscoding(media.id, authentication.admin?.id)
                        : await cancelVideoTranscoding(media.id, authentication.admin?.id);
    setTranscodingActionPending(false);

    if (response.code === 1) {
      onTranscoded(media.id);
    } else {
      console.error(`Impossible d'annuler le transcodage du média ID ${media.id} : ${response.comment}`);
    }
  };



  useEffect(() => {

    // `state === -1` signifie « pas transcodé », ce qui est aussi l'état d'un média
    // dont le fichier est encore en train de monter. Sonder dès maintenant, c'est
    // interroger le microservice sur un travail qu'on ne lui a pas encore confié —
    // et afficher son 0 %. On attend donc la fin du téléversement.
    if ( isVideoOrSlideshow(media) && ( media.state === -1 ) && ! uploadEnCours ) {
      // Deux sondes peuvent se chevaucher (une par seconde, réponse parfois plus lente) :
      // sans ce drapeau chacune appelait onTranscoded, donc rechargeait la liste deux fois.
      let termine = false;
      const fetchProgress = async () => {
        // Accès direct au microservice de transcodage en priorité (progression live, sans
        // repasser par Symfony) ; retombe sur Symfony si la route n'est pas (encore/plus)
        // enregistrée auprès de HAProxy (404/503/timeout).
        let transcodingStatus = await getVideoTranscodingProgressDirect(media.uuid);

        // code === 1 est le seul indicateur de succès (BackendResponse) ; handleError()
        // renvoie des codes négatifs (-101/-102) qui restent "truthy" en JS, donc un simple
        // `!transcodingStatus.code` ne détecte jamais l'échec de l'accès direct et empêche le
        // repli vers Symfony de se déclencher.
        if (transcodingStatus.code !== 1) {
          transcodingStatus = await getVideoTranscodingProgress(media.id);
        }

        if (termine) return;

        console.log ('transcodingStatus   = ', transcodingStatus)
        console.log ('transcodingProgress = ', transcodingProgress)

        if (transcodingStatus.code === 1) {
          const progress = Math.floor(transcodingStatus.payload.progress);
          setTranscodingProgress(progress);

          // Copie dans le bandeau global (TaskManager.tsx) — cf. store/tasksManagment.
          // La tâche reste visible même une fois « Terminé », jusqu'à ce qu'on la
          // referme là-bas, comme pour un upload.
          dispatch(upsertTranscodingTask(
            media.id, media.name, progress,
            transcodingStatus.payload.transcoded ? TranscodingTaskStatus.COMPLETED : TranscodingTaskStatus.TRANSCODING
          ));

          if (transcodingStatus.payload.transcoded) {
            termine = true;
            clearInterval(interval);
            onTranscoded(media.id);
          }
        }
      };

      fetchProgress();
      const interval = setInterval(fetchProgress, 1000);
      return () => { termine = true; clearInterval(interval); };
    }
  }, [media, uploadEnCours]);

  // Même bandeau, pour le cas d'échec (state === -2) : ce n'est pas l'effet
  // ci-dessus qui le détecte, puisqu'il ne sonde que tant que state === -1.
  useEffect(() => {
    if (isFailed) {
      dispatch(upsertTranscodingTask(media.id, media.name, transcodingProgress ?? 0, TranscodingTaskStatus.FAILED));
    }
  }, [isFailed, media.id, media.name]);

  const getMediaTypeIconName = () => {
    switch (media.mediaType)
	 {
      case "Image"          : return "mediaTypeImage";
      case "Sound"          : return "mediaTypeSound";
      case "Video"          : return "mediaTypeVideo";
      case "AudioSlideshow" : return "mediaTypeAudioSlideshow";
      default               : return "void";
     }
  };

  const getMediaTypeIconColor = () => {
    switch (media.mediaType)
	 {
      case "Image"          : return "#0CEC4E";
      case "Sound"          : return "#DB0CCE";
      case "Video"          : return "#0CECCE";
      case "AudioSlideshow" : return "#ECA70C";
      default               : return "void";
     }
  };



  const checkAssociatedProduct = async (): Promise<boolean> => {

    const associatedProduct = await retrieveProductById ( media.associatedProductIds[0] )

    return associatedProduct?.active ?? false
  }

  // Vues : seules les vidéos en ont.
  const videoViews = media.mediaType === 'Video'
    ? { kleon   : (media as unknown as Video).numberOfViews ?? 0,
        youtube : (media as unknown as Video).numberOfViewsFromYoutube ?? 0 }
    : null;

  const [isPaid, setIsPaid] = useState(media.associatedProductIds.length > 0);

  // La mise en ligne d'une sortie programmée active aussi le produit côté serveur (cron) :
  // le cache Redux garde l'ancien `active`, on le relit quand la vidéo passe active.
  const wasActive = useRef(media.active);

  useEffect(() => {
    const becameActive = !wasActive.current && media.active;
    wasActive.current = media.active;

    if (media.associatedProductIds.length === 0) {
      setIsPaid(false);
      return;
    }

    const fresh = becameActive ? refreshProductById(media.associatedProductIds[0]) : Promise.resolve();
    fresh.then(checkAssociatedProduct).then(setIsPaid);
  }, [media.associatedProductIds, media.active]);

  // Une sortie programmée n'est pas encore visible sur le portail : `publicPortal` dit seulement
  // où elle sortira.
  const isPublic = media.publicPortal && !(media.activeScheduled && !media.active);


  // Seules vidéo et diaporama passent par un transcodage : une image ou un son garde
  // `transcoded=false` (valeur par défaut du front), ce qui les grisait tous.
  const isProcessing = isVideoOrSlideshow(media) && ( (media.transcoded === false || media.state === -1) && transcodingProgress !== 100 )




const handleThumbnailClick = () => {
  if (media.mediaType === "Video" && media.hasSalesCampaign) {
    setShowVideoChoice(true);
  } else {
    onView(index, false);
  }
};


const renderMedia = () => {

  switch (media.mediaType) {

    // Un son n'a pas de vignette : `thumbnail.webp` n'est généré qu'à partir d'une image,
    // et le champ `thumbnail` reste vide en base pour ce type. On affiche donc une icône —
    // mais dans le même cadre, avec le même clic et le même grisage que les autres lignes,
    // qui n'étaient ni cliquables ni grisées tant qu'elles passaient par MediaThumbnail.
    case "Sound":

      return (
        <div
          onClick={!isProcessing ? () => handleThumbnailClick() : undefined}
          className={classNames(styles.thumbnail, styles.thumbnailIconOnly, { [styles.thumbnailDisabled]: isProcessing })}
        >
          <Icon name="musicalNotes" width={32} height={32} color="var(--color-0)" hoverable={false} clickable={false} />
        </div>
      );

    // L'image, elle, a bien sa vignette au même endroit que la vidéo et le diaporama :
    // même rendu qu'eux, ce qui évite au passage le téléchargement du blob complet que
    // MediaThumbnail déclenchait en repli, une fois par ligne affichée.
    default:

      return (
        <img
          src={`${WWW_URLS.MediaServer}${media.publicRef}/images/thumbnail.webp`}
          alt={media.name}
          onClick={!isProcessing ? () => handleThumbnailClick() : undefined}
          className={classNames(styles.thumbnail, { [styles.thumbnailDisabled]: isProcessing })}
        />
      );
  }
};



  return (
    <div className={classNames(styles.row)}>
	
	 {
	  showVideoChoice && 
	  (
       <div className={styles.qrOverlay}>
         <div className={styles.qrModal}>
           <div className = {styles.qrOption}
                onClick   = { () => {
                                      setShowVideoChoice(false);
                                      onView(index, false); // Sans QR
                            }       }  >
             Sans QRcode
           </div>

           <div className = {styles.qrOption}
                onClick   = { () => {
                                      setShowVideoChoice(false);
                                      onView(index, true); // Avec QR
                            }       }  >
             Avec QRcode
           </div>
         </div>
       </div>
      )
	 }
		
      <div className={classNames(styles.column_1, styles.standard)}>
	    {media.id}
	  </div>
      <div className={classNames(styles.column_2, styles.standard)}>
	    <div className={styles.iconButton} >
          <Icon name={getMediaTypeIconName()} backgroundColor={getMediaTypeIconColor()} height={40} width={40} hoverable={false} clickable={false} />
        </div>
      </div>
	  {/*
      <div className={classNames(styles.column_3, styles.standard)}>
        {
		 media.hasSalesCampaign &&
		 <Icon name       = "filledStar"
		       color      = "var(--color-Emphasis)"
			   hoverColor = "var(--color-Emphasis)" />
		}
      </div>
	  */}
      <div className={classNames(styles.column_4, isVideoOrSlideshow(media)
                                                    ? ( ( media.state !== -1 || media.transcoded ) ? styles.transcoded : styles.not_transcoded)
                                                    : styles.standard)}>

        {
		 uploadProgress !== null &&
		 uploadProgress < 100 &&
		 (
          <div className={styles.progressOverlay}>
            <span className={styles.progressLabel}>Upload</span>
            <span className={styles.progressText}>{uploadProgress}%</span>
          </div>
         )
		}

        {
		 transcodingProgress !== null &&
		 transcodingProgress < 100 &&
		 (uploadProgress === null || uploadProgress >= 100) &&
		 (
          <div className={styles.progressOverlay}>
            <span className={styles.progressLabel}>Transcodage</span>
            <span className={styles.progressText}>{transcodingProgress}%</span>
          </div>
         )
		}

        {
         isFailed &&
         (
          <div className={classNames(styles.progressOverlay, styles.errorOverlay)}>
            <span className={styles.progressLabel}>Échec du transcodage</span>
            <div className={styles.errorActions}>
              <div className={styles.iconButton} onClick={!transcodingActionPending ? handleRetryTranscoding : undefined} title="Relancer le transcodage">
                <Icon name       = "RefreshCw"
                      color      = "var(--color-Icon-FG)"
                      hoverColor = "var(--color-Icon-hover-FG)"
                      width      = {18}
                      height     = {18} />
              </div>
              <div className={styles.iconButton} onClick={!transcodingActionPending ? handleCancelTranscoding : undefined} title="Annuler le transcodage">
                <Icon name       = "X"
                      color      = "var(--color-Icon-FG)"
                      hoverColor = "var(--color-Icon-hover-FG)"
                      width      = {18}
                      height     = {18} />
              </div>
            </div>
          </div>
         )
        }

{renderMedia()}


      </div>

      <div className={classNames(styles.column_5, styles.standard)}>
	    {media.name}
	  </div>
      {/* Durée : seules les vidéos et les diaporamas sonores en ont une en base ; images et sons affichent un tiret. */}
      <div className={classNames(styles.column_12, styles.standard)}>
        {durationLabel}
      </div>

      {/*
        Place occupée sur le disque, telle que le serveur l'a mesurée. Un média
        jamais mesuré (`storageComputedAt` à null) affiche un tiret grisé : son
        `storageBytes` vaut 0, ce qui ne veut pas dire qu'il ne pèse rien.
      */}
      <div className={classNames(styles.column_11, styles.standard, { [styles.sizeUnknown]: media.storageComputedAt === null })}
           title={media.storageComputedAt !== null
                    ? `${media.multiview ? `Versions transcodées + enregistrements d'origine (${formatStorageSize(media.storageOriginBytes ?? 0, media.storageComputedAt)}). Supprimer la vidéo ne libère pas ces derniers. ` : ''}Mesuré le ${media.storageComputedAt.toLocaleDateString()}`
                    : "Taille jamais mesurée pour ce média"}>
        {formatStorageSize(media.storageBytes, media.storageComputedAt)}
      </div>
      <div className={classNames(styles.column_14, styles.standard, { [styles.sizeUnknown]: !videoViews })}
           title={videoViews ? `Kleon : ${videoViews.kleon.toLocaleString('fr-FR')} · YouTube : ${videoViews.youtube.toLocaleString('fr-FR')}` : undefined}>
        {videoViews ? (videoViews.kleon + videoViews.youtube).toLocaleString('fr-FR') : '—'}
      </div>

      <div className={classNames(styles.column_6, styles.standard)}>
        {
		 // L'adresse s'affiche toujours, qu'elle soit listée ou non : hors portail, le lien
		 // (slug exigé côté serveur) est la seule voie d'accès ; inactive, le
		 // lien ne marche pas tant que la vidéo n'est pas activée, libellé ⏸.
		 media.mediaType === 'Video' && media.slug &&
		 (
          <>
            {!media.active && <span title="Vidéo inactive : ce lien ne fonctionnera qu'une fois la vidéo activée">⏸&nbsp;</span>}
            <a href      = {`${WWW_URLS.portal_front}/${media.slug}`}
               target    = "_blank"
               rel       = "noopener noreferrer"
               className = {styles.videoLink} >
              {`${WWW_URLS.portal_front}/${media.slug}`}
            </a>
            <span className={styles.extLinkButton}
                  onClick={() => window.open(`${WWW_URLS.portal_front}/${media.slug}`, '_blank', 'noopener,noreferrer')} >
              <svg viewBox="0 0 24 24" fill="none" stroke="#93C5FD" strokeWidth="2" width="14" height="14">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </span>
          </>
         )
		}
      </div>
<div className={classNames(styles.column_7, styles.standard)}>
  <div className={styles.iconButton} onClick={() => onEdit(index)}>
    <Icon name       = "pen"
          color      = "var(--color-Icon-FG)"
          hoverColor = "var(--color-Icon-hover-FG)" 
          width      = {20}
          height     = {20}/>
  </div>
  <div className={styles.iconButton} onClick={() => onDelete(media)}>
    <Icon name       = "trashCan"
          color      = "var(--color-Icon-FG)"
          hoverColor = "var(--color-Icon-hover-FG)"
          width      = {20}
          height     = {20} />
  </div>
</div>


      <div className={classNames(styles.column_8, styles.standard)}>
        <div className={classNames(styles.statusDot, (media.active && ( media.transcoded && isVideoOrSlideshow(media)  || !isVideoOrSlideshow(media) ) ) ? styles.statusDotOk : styles.statusDotNok)}>
          <Icon name       = "disk"
		        color      = { (media.active && ( media.transcoded && isVideoOrSlideshow(media)  || !isVideoOrSlideshow(media) ) ) ? "var(--color-OK)" : "var(--color-NOK)"}
			    width      = {20}
			    height     = {16}
			    hoverable  = {false}
			    clickable  = {false} />
        </div>
      </div>
      <div className={classNames(styles.column_9, styles.standard)}>
        <div className={classNames(styles.statusDot, isPaid ? styles.statusDotOk : styles.statusDotNok)}>
          <Icon name       = "disk"
		        color      = { isPaid ? "var(--color-OK)" : "var(--color-NOK)"}
			    width      = {20}
			    height     = {16}
			    hoverable  = {false}
			    clickable  = {false} />
        </div>
      </div>
      <div className={classNames(styles.column_10, styles.standard)}>
        <div className={classNames(styles.statusDot, isPublic ? styles.statusDotOk : styles.statusDotNok)}>
          <Icon name       = "disk"
		        color      = {isPublic ? "var(--color-OK)" : "var(--color-NOK)"}
			    width      = {20}
			    height     = {16}
			    hoverable  = {false}
			    clickable  = {false} />
        </div>
      </div>
      <div className={classNames(styles.column_13, styles.standard)}>
        {media.multiview && <span className={styles.multiviewBadge}>Multi-angle</span>}
      </div>
    </div>
  );
};

export default MediaRow;